import { ConversionAttribution } from '@/common/model/recommend/conversion'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import { apiPostFetcher, apiPutFetcher } from '@/common/api/fetcher'
import { useWidgetListApi } from '@/common/api/modules/widget'
import {
  CreateDistributionResponse,
  CreateDistributionVars,
  UpdateDistributionResponse,
  UpdateDistributionVars
} from '@/common/api/params/recommendationDistribution'
import { ApiPath } from '@/common/api/path'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import {
  abCampaignGoalMetricName,
  abCampaignTargetDeviceName,
  abCampaignTargetSegmentsName
} from '@/common/app/modules/recommend/abCampaign'
import {
  distributionStatusBadgeType,
  distributionStatusName,
  isDistributionReadOnly
} from '@/common/app/modules/recommend/recommendationDistribution'
import { widgetDisplayLogicName } from '@/common/app/modules/recommend/widget'
import { distributionEditPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowSavedToast } from '@/common/app/toast'
import { WidgetBadgeChoice } from '@/common/form/choice'
import { registerNumberOption } from '@/common/form/register'
import {
  ABCampaignGoalMetric,
  ABCampaignSegments,
  ABCampaignTargetDevice
} from '@/common/model/recommend/abCampaign'
import {
  RecommendationDistribution,
  RecommendationDistributionStatus
} from '@/common/model/recommend/recommendationDistribution'
import { WidgetStatus } from '@/common/model/recommend/widget'
import { preventKeyDown } from '@/common/util/keyboard'

import { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import { FieldType, TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content, { ContentType } from '@/components/view/layouts/page/Content'
import ContentSideFrame from '@/components/view/layouts/page/ContentSideFrame'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import Side from '@/components/view/layouts/page/Side'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  DistributionFormSchemaType,
  convertDistributionSchemaToVars,
  convertDistributionToSchema,
  distributionNameLabelMapping,
  distributionSchema,
  makeDefaultDistributionSchema
} from '@/components/container/form/modules/recommend/distribution/recommendationDistributionSchema'
import FormCheckBox from '@/components/container/form/widgets/FormCheckBox'
import FormCheckBoxes from '@/components/container/form/widgets/FormCheckBoxes'
import FormDateTextField, {
  FormDateTextFieldType
} from '@/components/container/form/widgets/FormDateTextField'
import FormDisclosureSelector from '@/components/container/form/widgets/FormDisclosureSelector'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormRadioButton from '@/components/container/form/widgets/FormRadioButton'
import FormSelector from '@/components/container/form/widgets/FormSelector'
import FormTextField, { FormTextFieldType } from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'
import AppTitleBadge from '@/components/container/layout/AppTitleBadge'

interface Props {
  distribution?: RecommendationDistribution
  onRefetch?(): void
}

const VARIANT_COUNT_CHOICES = [2, 3, 4, 5].map((n) => ({ name: `${n}パターン`, value: n }))

const GOAL_METRIC_CHOICES = Object.values(ABCampaignGoalMetric).map((v) => ({
  name: abCampaignGoalMetricName(v),
  value: v
}))

// A/Bの勝者判定に使うコンバージョン種別。同じ受注が両方に記録されうるため、
// パターン間の比較は必ず同一種別どうしで行う
const CV_ATTRIBUTION_CHOICES = [
  { name: '直接CV（クリックした対象の商品が購入された）', value: ConversionAttribution.DIRECT },
  { name: 'アシストCV（クリック後に受注が発生した）', value: ConversionAttribution.ASSIST }
]

const TARGET_DEVICE_CHOICES = Object.values(ABCampaignTargetDevice).map((v) => ({
  name: abCampaignTargetDeviceName(v),
  value: v
}))

const TARGET_SEGMENT_CHOICES = Object.values(ABCampaignSegments).map((v) => ({
  name: abCampaignTargetSegmentsName(v),
  value: v
}))

const CONFIDENCE_CHOICES = [
  { name: '90%', value: 0.9 },
  { name: '95%', value: 0.95 },
  { name: '99%', value: 0.99 }
]

const patternColor = (index: number) => {
  const colors = [
    TextColor.SORA,
    TextColor.GREEN,
    TextColor.ORANGE,
    TextColor.PURPLE,
    TextColor.SKY
  ]
  return colors[index % colors.length]
}

const RecommendationDistributionForm = ({ distribution, onRefetch }: Props) => {
  const navigate = useNavigate()
  const siteId = useSiteId()
  const readOnly = distribution
    ? !!distribution.ab_campaign?.completed_at || isDistributionReadOnly(distribution.status)
    : false

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting },
    reset,
    setValue
  } = useForm<DistributionFormSchemaType>({
    resolver: zodResolver(distributionSchema),
    values: distribution
      ? convertDistributionToSchema(distribution)
      : makeDefaultDistributionSchema(siteId),
    mode: 'onChange'
  })

  const { data: widgets } = useWidgetListApi({ site_id: siteId })
  const widgetChoices = (widgets ?? [])
    .filter((w) => w.status !== WidgetStatus.ARCHIVED)
    .map((w) => ({ name: w.name, value: String(w.id) }))

  const findWidget = (widgetId?: string) =>
    widgetId ? (widgets ?? []).find((w) => String(w.id) === widgetId) : undefined

  const abEnabled = useWatch({ control, name: 'abEnabled' })
  const variantCount = useWatch({ control, name: 'variantCount' })
  const variants = useWatch({ control, name: 'variants' })
  const goalMetric = useWatch({ control, name: 'goalMetric' })

  const activeCount = abEnabled ? variantCount : 1
  const trafficTotal = (variants ?? [])
    .slice(0, activeCount)
    .reduce((sum, v) => sum + (v?.abTraffic || 0), 0)

  const distributeEvenly = () => {
    const base = Math.floor(100 / variantCount)
    for (let i = 0; i < variantCount; i++) {
      const value = i === variantCount - 1 ? 100 - base * (variantCount - 1) : base
      setValue(`variants.${i}.abTraffic`, value, { shouldDirty: true, shouldValidate: true })
    }
  }

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, distributionNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: !!distribution })

  const { trigger } = useCallMutation<
    CreateDistributionResponse | UpdateDistributionResponse,
    CreateDistributionVars | UpdateDistributionVars
  >(
    distribution ? ApiPath.UPDATE_DISTRIBUTION : ApiPath.CREATE_DISTRIBUTION,
    {
      onSuccess: (response) => {
        showSaved(response.name)
        distribution && onRefetch
          ? onRefetch()
          : navigate(distributionEditPath(siteId, response.id))
      }
    },
    distribution ? apiPutFetcher : apiPostFetcher
  )

  const onSubmit: SubmitHandler<DistributionFormSchemaType> = (data) =>
    trigger(convertDistributionSchemaToVars(data))

  // ステータス選択肢（A/Bテスト中は編集時かつA/Bテスト有効時のみ／配信終了は停止フローで遷移）
  const statusChoices: WidgetBadgeChoice<string>[] = [
    {
      name: distributionStatusName(RecommendationDistributionStatus.DRAFT),
      value: RecommendationDistributionStatus.DRAFT,
      badgeType: distributionStatusBadgeType(RecommendationDistributionStatus.DRAFT)
    },
    {
      name: distributionStatusName(RecommendationDistributionStatus.RUNNING),
      value: RecommendationDistributionStatus.RUNNING,
      badgeType: distributionStatusBadgeType(RecommendationDistributionStatus.RUNNING)
    },
    {
      name: distributionStatusName(RecommendationDistributionStatus.TESTING),
      value: RecommendationDistributionStatus.TESTING,
      badgeType: distributionStatusBadgeType(RecommendationDistributionStatus.TESTING),
      disabled: !distribution || !abEnabled
    },
    {
      name: distributionStatusName(RecommendationDistributionStatus.ENDED),
      value: RecommendationDistributionStatus.ENDED,
      badgeType: distributionStatusBadgeType(RecommendationDistributionStatus.ENDED),
      disabled: !distribution
    }
  ]

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={distribution ? distribution.name : '配信を新規作成する'}
        badgeNode={<AppTitleBadge type='recommend' />}
      />
      {isDirty && !readOnly && (
        <TopBar
          status={distribution ? TopBarStatus.CHANGE : TopBarStatus.NEW}
          captionText={distribution ? '未反映の変更があります' : '登録されていない新しい配信'}
          onCancel={() =>
            reset(
              distribution
                ? convertDistributionToSchema(distribution)
                : makeDefaultDistributionSchema(siteId)
            )
          }
          loading={isSubmitting}
          withSide
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <ContentSideFrame>
        <Content type={ContentType.TWO_COLUMNS_LEFT}>
          <Area>
            <Heading>基本設定</Heading>
            <AreaContent>
              <FormRow>
                <FormLabel required id='name'>
                  配信管理名
                </FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormTextField
                    register={register('name')}
                    fieldType={FieldType.REQUIRED}
                    error={!!errors.name?.message}
                    size={TextBoxSize.FULL}
                    placeholder='サマーキャンペーン配信'
                  />
                  <FormError message={errors.name?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel required id='code'>
                  埋め込みコード
                </FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormTextField
                    register={register('code')}
                    fieldType={FieldType.REQUIRED}
                    error={!!errors.code?.message}
                    size={TextBoxSize.FULL}
                    placeholder='RECOMMEND_01'
                    disabled={readOnly}
                  />
                  <FormError message={errors.code?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>A/Bテスト</FormLabel>
                <FormCheckBox
                  control={control}
                  name={'abEnabled'}
                  type={CheckBoxType.TOGGLE_TEXT}
                  toggleText={{ left: '行う', right: '行わない' }}
                  disabled={readOnly}
                />
              </FormRow>
              {abEnabled && (
                <>
                  <FormRow>
                    <FormLabel>設定数</FormLabel>
                    <FormSelector
                      register={register('variantCount', registerNumberOption)}
                      choices={VARIANT_COUNT_CHOICES}
                      disabled={readOnly}
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>
                      <ItemAlign gapY={4}>
                        トラフィック配分
                        <Button
                          color={ButtonColor.SECONDARY}
                          onClick={distributeEvenly}
                          disabled={readOnly}
                        >
                          均等配分
                        </Button>
                      </ItemAlign>
                    </FormLabel>
                    <FormRow direction={'vertical'} gap={2}>
                      <FormRow gap={8}>
                        {[...Array(variantCount)].map((_, index) => (
                          <FormRow
                            key={index}
                            direction='vertical'
                            gap={2}
                            size={TextBoxSize.FIT}
                            align='center'
                          >
                            <Text size={12} weight='bold' color={patternColor(index)}>
                              {`パターン${String.fromCharCode(65 + index)}`}
                            </Text>
                            <FormRow gap={2} align='center' size={TextBoxSize.FIT}>
                              <FormTextField
                                register={register(
                                  `variants.${index}.abTraffic`,
                                  registerNumberOption
                                )}
                                size={TextBoxSize.X_SMALL}
                                type={FormTextFieldType.NUMBER}
                                disabled={readOnly}
                              />
                              <Text>%</Text>
                            </FormRow>
                          </FormRow>
                        ))}
                        <FormRow direction='vertical' gap={8} size={TextBoxSize.FIT} align='center'>
                          <Text size={12} weight='bold'>
                            合計
                          </Text>
                          <Text>{trafficTotal}%</Text>
                        </FormRow>
                      </FormRow>
                      <FormError message={errors.variants?.message} />
                    </FormRow>
                  </FormRow>
                  <FormRow>
                    <FormLabel>KPI目標</FormLabel>
                    <FormRadioButton
                      control={control}
                      name={'goalMetric'}
                      choices={GOAL_METRIC_CHOICES}
                      direction='horizon'
                      gap={12}
                      disabled={readOnly}
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>コンバージョン種別</FormLabel>
                    <FormRadioButton
                      control={control}
                      name={'cvAttribution'}
                      choices={CV_ATTRIBUTION_CHOICES}
                      direction='horizon'
                      gap={12}
                      // KPI目標がクリック率のときは判定に使わないため無効化する
                      disabled={readOnly || goalMetric !== ABCampaignGoalMetric.CVR}
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>ターゲットデバイス</FormLabel>
                    <FormCheckBoxes
                      control={control}
                      name={'targetDevices'}
                      choices={TARGET_DEVICE_CHOICES}
                      disabled={readOnly}
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>ターゲットセグメント</FormLabel>
                    <FormRadioButton
                      control={control}
                      name={'targetSegment'}
                      choices={TARGET_SEGMENT_CHOICES}
                      direction='horizon'
                      gap={12}
                      disabled={readOnly}
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>最小サンプル数</FormLabel>
                    <FormRow direction={'vertical'} gap={2}>
                      <FormTextField
                        register={register('minSamplePerVariant', registerNumberOption)}
                        size={TextBoxSize.X_SMALL}
                        type={FormTextFieldType.NUMBER}
                        error={!!errors.minSamplePerVariant?.message}
                        disabled={readOnly}
                      />
                      <FormError message={errors.minSamplePerVariant?.message} />
                    </FormRow>
                  </FormRow>
                  <FormRow>
                    <FormLabel>信頼度</FormLabel>
                    <FormSelector
                      register={register('confidenceLevel', registerNumberOption)}
                      choices={CONFIDENCE_CHOICES}
                      disabled={readOnly}
                    />
                  </FormRow>
                </>
              )}
            </AreaContent>
          </Area>

          <Area noBorder>
            <Heading>配信パーツ設定</Heading>
            <AreaContent>
              {[...Array(activeCount)].map((_, index) => (
                <FormRow key={index} direction='vertical' gap={16}>
                  {abEnabled && (
                    <Text
                      size={14}
                      weight='bold'
                      color={patternColor(index)}
                    >{`パターン${String.fromCharCode(65 + index)}`}</Text>
                  )}
                  <FormRow key={index}>
                    <FormLabel id={`variants.${index}.widgetId`}>表示パーツ</FormLabel>
                    <FormRow direction={'vertical'} gap={2}>
                      <ItemAlign gapX={8} align='center'>
                        <FormSelector
                          register={register(`variants.${index}.widgetId`)}
                          choices={widgetChoices}
                          value={variants?.[index]?.widgetId}
                          error={!!errors.variants?.[index]?.widgetId?.message}
                          noSelectable
                          noSelectName='未選択'
                          disabled={readOnly}
                        />
                        {(() => {
                          const widget = findWidget(variants?.[index]?.widgetId)
                          return widget ? (
                            <Text size={12} color={TextColor.SECONDARY} nowrap>
                              {widgetDisplayLogicName(widget.display_logic)}
                            </Text>
                          ) : null
                        })()}
                      </ItemAlign>
                      <FormError message={errors.variants?.[index]?.widgetId?.message} />
                    </FormRow>
                  </FormRow>
                </FormRow>
              ))}
            </AreaContent>
          </Area>
        </Content>
        <Side>
          <Area>
            <Heading>利用状況</Heading>
            <AreaContent>
              <FormRow align='center'>
                <FormLabel fixWidth={80}>ステータス</FormLabel>
                <FormDisclosureSelector
                  control={control}
                  name='status'
                  choices={statusChoices}
                  disabled={readOnly}
                />
              </FormRow>
              <FormRow>
                <FormLabel id='distributesAt' fixWidth={80}>
                  配信開始
                </FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormDateTextField
                    control={control}
                    name='distributesAt'
                    type={FormDateTextFieldType.DATETIME}
                    disabled={readOnly}
                  />
                  <FormError message={errors.distributesAt?.message} />
                </FormRow>
              </FormRow>
            </AreaContent>
          </Area>
        </Side>
      </ContentSideFrame>
      {!readOnly && (
        <PageBottom>
          <Button
            type='submit'
            size='large'
            color={ButtonColor.PRIMARY_BRAND}
            loading={isSubmitting}
          >
            {distribution ? '保存する' : '登録する'}
          </Button>
        </PageBottom>
      )}
    </form>
  )
}

export default RecommendationDistributionForm
